import type { ID, ResourceResponse, ThunkReturnType } from "types";
import { createAsyncThunk } from "infrastructure/store/lib";
import type { AsyncThunk, RootState } from "infrastructure/store";
import { ApiState } from "infrastructure/api";
import { ApiRouteTypes } from "consts/enpoints/api";
import { payloadCreator } from "util/api";
import { concat } from "util/support";
import { sliceToken } from "./state";
import type { Setup, SetupResponse, SetupsResponse, } from "./types";
import {
    getSetupByIdentifier,
    getSetups,
    updateOrCreateSetup,
    deleteSetup
} from "./api";

export const getSetupsThunk: AsyncThunk<
    ThunkReturnType<SetupsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetSetups]),
    payloadCreator(getSetups),
    {
        condition: (_, { getState }) => {
            const { setups } = getState() as RootState;

            return ![ApiState.Pending].includes(setups.tableLoadingState);
        }
    }
);

export const getSetupByIdentifierThunk: AsyncThunk<
    ThunkReturnType<SetupResponse<string>>,
    Setup<string>['identifier'],
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetSetups, 'byIdentifier']),
    payloadCreator(getSetupByIdentifier),
    {
        condition: (identifier, { getState }) => {
            const { setups } = getState() as RootState;

            return ![ApiState.Succeeded, ApiState.Pending].includes(setups[identifier].loadingState);
        }
    }
);

export const updateOrCreateSetupThunk: AsyncThunk<
    ThunkReturnType<SetupResponse<string>>,
    Partial<Setup<string>>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutSetups]),
    payloadCreator(updateOrCreateSetup),
    {
        condition: (_, { getState }) => {
            const { setups } = getState() as RootState;

            return ![ApiState.Pending].includes(setups.tableLoadingState);
        }
    }
);

export const deleteSetupThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<ID>>,
    Pick<Setup<string>, 'coreId' | 'MOR'>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.DeleteSetup]),
    payloadCreator(deleteSetup),
    {
        condition: (_, { getState }) => {
            const { setups } = getState() as RootState;

            return ![ApiState.Pending].includes(setups.tableLoadingState);
        }
    }
);
